import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import Transloadit, { TransloaditOptions, AssemblyResponse } from '@uppy/transloadit';
import ImageEditor from '@uppy/image-editor';
import type { Locale } from '@uppy/utils';
import en_US from '@uppy/locales/lib/en_US';
import sv_SE from '@uppy/locales/lib/sv_SE';

/**
 * Result from a completed Transloadit assembly
 */
interface TransloaditAssemblyResult {
    [key: string]: Array<{
        ssl_url: string;
        url: string;
        name: string;
        size: number;
        mime: string;
    }>;
}

/**
 * Upload result with success/failure tracking
 */
interface UploadSummary {
    succeeded: number;
    failed: number;
    total: number;
}

type UppyLocale = Locale<0 | 1>;

function createAssetKey(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

const uppyResetRetryDelayMs = 50;
const uppyResetMaxAttempts = 100;
let isUppyResetScheduled = false;

const allowedImageMimeTypes = [
    'image/jpeg',
    'image/jpg',
    'image/pjpeg',
    'image/png',
    'image/x-png',
    'image/webp',
    'image/avif',
    'image/bmp',
    'image/tiff',
    'image/svg+xml',
    'image/heic',
    'image/heif'
];

// Single Uppy instance shared across all uploads
const uppy = new Uppy<Record<string, unknown>>({
    restrictions: {
        allowedFileTypes: allowedImageMimeTypes
    },
    locale: en_US
});

uppy
    .use(Dashboard, { 
        inline: false,
        proudlyDisplayPoweredByUppy: false
    })
    .use(ImageEditor, { 
        quality: 0.9 
    })
    .use(Transloadit, {
        waitForEncoding: true,
        assemblyOptions: {
            params: {}
        }
    } as TransloaditOptions<Record<string, unknown>, Record<string, never>>);

function configureTransloaditSigned(paramsJson: string, signature: string): void {
    const transloadit = uppy.getPlugin('Transloadit');
    if (!transloadit) {
        throw new Error('Transloadit plugin not found');
    }

    const transloaditPlugin = transloadit as any;
    // Keep params as the exact signed JSON string so Uppy sends the same payload bytes.
    transloaditPlugin.opts.assemblyOptions.params = paramsJson;
    transloaditPlugin.opts.assemblyOptions.signature = signature;
}

/**
 * Open the Uppy dashboard modal
 */
function resolveDashboardTarget(target?: string): string | undefined {
    const candidates = [target, '#loci', '#app', 'body'];

    for (const candidate of candidates) {
        if (!candidate) {
            continue;
        }

        if (document.querySelector(candidate)) {
            return candidate;
        }
    }

    return undefined;
}

function openDashboard(target?: string): void {
    const dashboard = uppy.getPlugin('Dashboard');
    if (!dashboard) {
        throw new Error('Dashboard plugin not found');
    }
    
    const dashboardPlugin = dashboard as any;
    const resolvedTarget = resolveDashboardTarget(target);
    if (resolvedTarget) {
        dashboardPlugin.opts.target = resolvedTarget;
    }
    else {
        delete dashboardPlugin.opts.target;
    }

    dashboardPlugin.openModal();
}

function mergeLocaleWithFallback(primary: UppyLocale, fallback: UppyLocale): UppyLocale {
    return {
        ...fallback,
        ...primary,
        pluralize: primary.pluralize ?? fallback.pluralize,
        strings: {
            ...(fallback.strings ?? {}),
            ...(primary.strings ?? {})
        }
    };
}

function normalizeLanguage(language?: string | null): 'sv' | 'en' | null {
    if (!language) {
        return null;
    }

    const normalized = language.trim().toLowerCase();
    if (!normalized) {
        return null;
    }

    if (normalized === 'sv' || normalized.startsWith('sv-')) {
        return 'sv';
    }

    if (normalized === 'en' || normalized.startsWith('en-')) {
        return 'en';
    }

    return null;
}

function resolveLanguage(explicitLanguage?: string): 'sv' | 'en' {
    return normalizeLanguage(explicitLanguage) ?? 'en';
}

function applyLocale(language?: string): void {
    const resolvedLanguage = resolveLanguage(language);
    const localeToUse = resolvedLanguage === 'sv'
        ? mergeLocaleWithFallback(sv_SE, en_US)
        : en_US;

    uppy.setOptions({
        locale: localeToUse
    });
}

/**
 * Create a promise that resolves when upload completes
 */
function createResultPromise(): Promise<TransloaditAssemblyResult> {
    return new Promise((resolve, reject) => {
        let isResolved = false;
        let succeededFiles = 0;
        let failedFiles = 0;
        
        // Track file upload progress
        const fileAddedHandler = (file: any) => {
            succeededFiles = 0;
            failedFiles = 0;

            if (!file?.id) {
                return;
            }

            const existingAssetKey = file.meta?.assetKey;
            if (typeof existingAssetKey === 'string' && existingAssetKey.length > 0) {
                return;
            }

            uppy.setFileMeta(file.id, { assetKey: createAssetKey() });
        };
        
        // Handle successful completion
        const completeHandler = (assembly: AssemblyResponse): void => {
            if (isResolved) return;
            isResolved = true;
            
            // Remove listeners first
            cleanup();
            
            const dashboard = uppy.getPlugin('Dashboard');
            if (dashboard) {
                (dashboard as any).closeModal();
            }
            
            const files = uppy.getFiles();
            succeededFiles = files.length;
            
            // Reset Uppy state only after active uploads are fully finished.
            scheduleUppyReset();
            
            const results = (assembly as { results?: TransloaditAssemblyResult }).results;
            if (!results) {
                reject(new Error('Upload completed without assembly results'));
                return;
            }

            resolve(results);
        };
        
        // Handle individual file upload success
        const uploadSuccessHandler = (_file: any, _response: any) => {
            succeededFiles++;
        };
        
        // Handle individual file upload errors
        const uploadErrorHandler = (_file: any, _error: Error) => {
            failedFiles++;
        };
        
        // Handle general errors (after all uploads attempted)
        const errorHandler = (_error: Error): void => {
            if (isResolved) return;
            isResolved = true;
            
            // Remove listeners first
            cleanup();
            
            const dashboard = uppy.getPlugin('Dashboard');
            if (dashboard) {
                (dashboard as any).closeModal();
            }
            
            const files = uppy.getFiles();
            const totalFiles = files.length;
            
            // If we're in the error handler and no files explicitly succeeded,
            // then all files failed (even if failedFiles wasn't incremented)
            if (succeededFiles === 0 && failedFiles === 0) {
                failedFiles = totalFiles;
            }
            
            // Reset Uppy state only after active uploads are fully finished.
            scheduleUppyReset();
            
            // Create a structured error with upload summary
            const summary: UploadSummary = {
                succeeded: succeededFiles,
                failed: failedFiles,
                total: totalFiles
            };
            
            const errorWithSummary = new Error(JSON.stringify(summary));
            errorWithSummary.name = 'UploadError';
            reject(errorWithSummary);
        };
        
        // Handle cancellation (when user explicitly cancels)
        const cancelHandler = (): void => {
            if (isResolved) return;
            isResolved = true;
            
            // Remove listeners first
            cleanup();
            
            const dashboard = uppy.getPlugin('Dashboard');
            if (dashboard) {
                (dashboard as any).closeModal();
            }
            
            // Reset Uppy state only after active uploads are fully finished.
            scheduleUppyReset();
            
            const error = new Error('Upload cancelled by user');
            error.name = 'UploadCancelled';
            reject(error);
        };
        
        // Handle modal close (when user clicks X or closes modal)
        const modalCloseHandler = (): void => {
            // Only reject if upload hasn't completed yet
            if (!isResolved) {
                isResolved = true;
                cleanup();
                
                // Reset Uppy state only after active uploads are fully finished.
                scheduleUppyReset();
                
                const error = new Error('Upload cancelled by user');
                error.name = 'UploadCancelled';
                reject(error);
            }
        };
        
        // Cleanup function to remove all listeners
        const cleanup = (): void => {
            uppy.off('file-added', fileAddedHandler);
            uppy.off('transloadit:complete', completeHandler);
            uppy.off('upload-success', uploadSuccessHandler);
            uppy.off('upload-error', uploadErrorHandler);
            uppy.off('error', errorHandler);
            uppy.off('cancel-all', cancelHandler);
            uppy.off('dashboard:modal-closed', modalCloseHandler);
        };
        
        uppy.on('file-added', fileAddedHandler);
        uppy.once('transloadit:complete', completeHandler);
        uppy.on('upload-success', uploadSuccessHandler);
        uppy.on('upload-error', uploadErrorHandler);
        uppy.on('error', errorHandler);
        uppy.once('cancel-all', cancelHandler);
        uppy.once('dashboard:modal-closed', modalCloseHandler);
    });
}

function clearFilesByIds(fileIds: Iterable<string>): void {
    const currentFileIds = new Set(uppy.getFiles().map(file => file.id));
    for (const fileId of fileIds) {
        if (currentFileIds.has(fileId)) {
            uppy.removeFile(fileId);
        }
    }
}

function hasActiveUploads(): boolean {
    const state = uppy.getState() as { currentUploads?: Record<string, unknown> };
    const currentUploads = state.currentUploads;
    return currentUploads !== undefined && Object.keys(currentUploads).length > 0;
}

function tryResetUppyState(attempt: number, fileIdsToReset: ReadonlySet<string>): void {
    if (!hasActiveUploads()) {
        clearFilesByIds(fileIdsToReset);
        isUppyResetScheduled = false;
        return;
    }

    if (attempt >= uppyResetMaxAttempts) {
        console.error('Timed out while waiting for uploads to finish before clearing Uppy files.');
        isUppyResetScheduled = false;
        return;
    }

    setTimeout(() => tryResetUppyState(attempt + 1, fileIdsToReset), uppyResetRetryDelayMs);
}

function scheduleUppyReset(): void {
    const fileIdsAtScheduleTime = new Set(uppy.getFiles().map(file => file.id));
    if (fileIdsAtScheduleTime.size === 0) {
        return;
    }

    if (isUppyResetScheduled) {
        return;
    }

    isUppyResetScheduled = true;
    tryResetUppyState(0, fileIdsAtScheduleTime);
}

export async function openPicker(
    paramsJson: string,
    signature: string,
    targetSelector?: string,
    language?: string
): Promise<TransloaditAssemblyResult> {
    configureTransloaditSigned(paramsJson, signature);
    applyLocale(language);
    openDashboard(targetSelector);
    return createResultPromise();
}
